import { faTrophy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { KillRace } from '../types'
import { formatUsd, resolvePhase, secondsUntil } from '../utils/killrace'
import { Countdown } from './Countdown'
import { GoArrow, PhaseBadge, phaseTone, PRIZE_GREEN, Skeleton } from './KillRaceUI'

const stopwatch = new URL('../assets/stopwatch_icon.svg', import.meta.url).href

/**
 * Six tracks, shared by the rows and by the header above them, so the numbers line up down the
 * page rather than each row finding its own alignment. Below `lg` the row is a two-line block and
 * this does not apply.
 *
 * Underscores, not commas: `grid-template-columns` is space-separated, and `minmax(0,1fr)` carries
 * a comma of its own that has to survive.
 */
const ROW_GRID = 'lg:grid lg:grid-cols-[minmax(0,1fr)_7rem_7rem_6rem_9.5rem_1.25rem] lg:items-center lg:gap-4'

/**
 * The list-view counterpart to `KillRaceCard`.
 *
 * The card leads with a banner, which is the right way to *browse* a handful of races and the
 * wrong way to *read* sixteen of them: at 375px it is a single column of ~430px tiles, so a full
 * schedule is several screens of artwork you have already seen twice. This row is ~72px on a phone
 * and ~52px on a desktop.
 *
 * Nothing is dropped except the banner. Name, phase, prize pool, paid places and the countdown all
 * survive — laid along the row instead of stacked over an image — and the phase keeps its colour
 * as a rim down the left edge, so a live race still reads as live at a glance.
 */
export const KillRaceRow = ({ race }: { race: KillRace }) => {
    const phase = resolvePhase(race.phase, race.begin, race.end)
    const isLive = phase === 'Live'
    const tone = phaseTone(phase)
    const servers = race.servers ?? []

    // Live races count down to their end, upcoming ones to their start — same rule as the card.
    const seconds = secondsUntil(isLive ? race.end : race.begin)

    return (
        <Link
            to={`/kill-race/info/${race.uuid}`}
            className='group block focus:outline-none focus-visible:opacity-80'
            aria-label={`${race.name}, ${phase}`}
        >
            <div
                className={twMerge(
                    'relative flex flex-col gap-2 border border-white/10 bg-white/[0.03] py-2.5 pl-4 pr-3',
                    'transition-colors hover:bg-white/[0.07] lg:gap-0 lg:py-2 lg:pr-4',
                    isLive && 'bg-[#00FF90]/[0.04] hover:bg-[#00FF90]/[0.08]',
                    ROW_GRID
                )}
            >
                {/* The phase, as a rim. A row has no banner to tint, and the badge alone put the
                    one fact that separates a live race from a scheduled one in the middle of a
                    column of text. */}
                <span className='absolute inset-y-0 left-0 w-[3px]' style={{ backgroundColor: tone.color }} />

                {/*
                  Both wrappers go `display: contents` at `lg`, so one set of children serves the
                  phone's two lines and the desktop's six columns. That reorders them — the arrow
                  is second in the DOM and sixth in the grid — so every cell states its own column
                  rather than relying on auto-placement, and `row-start-1` keeps them on one row.

                  The arrow shares the phone's *first* line rather than trailing the numbers on the
                  second. `$500` + `Top 25` + `6 days 02:05:57` + an arrow already wants more than
                  the 288px a 375px screen leaves, and the overflow would not show up in an
                  overflow check — `Panel` clips its descendants, so it would simply have gone
                  missing. The name it displaces has two lines to give.
                */}
                <div className='flex items-center justify-between gap-3 lg:contents'>
                    <div className='flex min-w-0 items-center gap-2.5 lg:col-start-1 lg:row-start-1 lg:gap-3'>
                        <PhaseBadge phase={phase} className='shrink-0' />
                        {/* Two lines on a phone, one on a desktop. `$500 - Top 25 [OPEN]` truncated
                            at the qualifier, which is the part that distinguishes one race from
                            another — the same reason the card uses `line-clamp-2`. */}
                        <h3
                            className='line-clamp-2 min-w-0 text-xs font-black uppercase leading-tight tracking-[0.14em] text-white/85 lg:line-clamp-1'
                            title={race.name}
                        >
                            {race.name}
                        </h3>
                    </div>

                    <GoArrow className='shrink-0 lg:col-start-6 lg:row-start-1 lg:justify-end' />
                </div>

                {/* `flex-wrap` is the backstop for the one case the line above cannot absorb: a
                    four-figure prize beside a three-figure ladder beside a multi-day countdown.
                    It grows a line instead of spilling out of the panel. */}
                <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-1 lg:contents'>
                    <span className='hidden truncate text-2xs font-bold uppercase tracking-[0.15em] text-white/40 lg:col-start-2 lg:row-start-1 lg:block'>
                        {race.mode || 'Global'}
                        {servers.length > 0 && <span className='text-white/25'> +{servers.length}</span>}
                    </span>

                    <span
                        className='text-base font-black leading-none tabular-nums lg:col-start-3 lg:row-start-1 lg:text-right lg:text-lg'
                        style={{ color: PRIZE_GREEN }}
                    >
                        {formatUsd(race.prize, 0)}
                    </span>

                    <span className='flex items-center gap-1.5 whitespace-nowrap text-xs font-black leading-none lg:col-start-4 lg:row-start-1 lg:justify-end'>
                        {/* The header names these columns on a desktop, so the icon that carries
                            the label on a phone comes off there. */}
                        <FontAwesomeIcon icon={faTrophy} className='text-2xs text-white/40 lg:hidden' />
                        Top {race.ladder_size}
                    </span>

                    <span className='flex items-center gap-1.5 whitespace-nowrap text-xs font-black leading-none tabular-nums lg:col-start-5 lg:row-start-1 lg:justify-end'>
                        <img src={stopwatch} alt='' className='h-3.5 w-3.5 shrink-0 opacity-50 lg:hidden' />
                        {seconds === null ? '—' : <Countdown seconds={seconds} />}
                    </span>
                </div>
            </div>
        </Link>
    )
}

/**
 * Column labels for the desktop list.
 *
 * `Ends in` / `Starts in` is a property of the bucket rather than of a row — the Live tab holds
 * only live races and the Upcoming tab only scheduled ones — so the header states it once instead
 * of every row repeating it.
 */
export const KillRaceRowHeader = ({ live }: { live: boolean }) => (
    <div className={twMerge('hidden px-4 pb-1', ROW_GRID)}>
        {['Race', 'Mode', 'Prize pool', 'Paid places', live ? 'Ends in' : 'Starts in', ''].map((label, i) => (
            <span
                key={label || i}
                className={twMerge('text-2xs font-bold uppercase tracking-[0.2em] text-white/35', i > 1 && 'text-right')}
            >
                {label}
            </span>
        ))}
    </div>
)

export const KillRaceRowSkeleton = () => (
    <div className={twMerge('flex flex-col gap-2 border border-white/10 py-2.5 pl-4 pr-3 lg:gap-0 lg:py-2', ROW_GRID)}>
        <Skeleton className='h-4 w-48' />
        <div className='flex items-center justify-between gap-3 lg:contents'>
            <Skeleton className='hidden h-3 w-16 lg:block' />
            <Skeleton className='h-4 w-20 lg:justify-self-end' />
            <Skeleton className='h-4 w-14 lg:justify-self-end' />
            <Skeleton className='h-4 w-24 lg:justify-self-end' />
            <Skeleton className='h-4 w-4 lg:justify-self-end' />
        </div>
    </div>
)
